import { useCallback, useMemo, useState } from "react"
import { GraphDirection } from "../../../types"
import { scrollBarSize } from "../../../constants"

type useGraphZoomProps = {
  direction: GraphDirection
	maxRow: number
	dataLength?: number
}
export const useGraphZoom = ({
  direction,
	maxRow,
	dataLength
}: useGraphZoomProps) => {
  const [zoomPos, setZoomPos] = useState<number>(0)

  const zoomStart = useMemo(
    () => {
      if (dataLength === undefined) return undefined
      if (maxRow <= 0 || maxRow >= dataLength) return undefined

      const zoomStart: number | undefined = zoomPos
      const page = ((maxRow - 1) / dataLength) * 100
      const zoomEnd = zoomStart + page
      if (zoomEnd <= 100) return zoomStart
      return 100 - page
    },
    [zoomPos, dataLength, maxRow]
  )
	const zoomEnd = useMemo(
		() => {
			if (zoomStart === undefined || dataLength === undefined) return undefined
      if (maxRow <= 0 || maxRow >= dataLength) return undefined
			return zoomStart + ((maxRow - 1) / dataLength) * 100
		},
		[zoomStart, dataLength, maxRow]
	)
  const onDataZoom = useCallback(
    (params?: { batch?: { start?: number }[] }) => {
      if (!params?.batch || (params.batch.length || 0) === 0) return
      setZoomPos(params.batch[0]?.start ?? 0)
    },
    [setZoomPos]
  )
  const dataZoom = useMemo(
    () => (zoomStart !== undefined) && (
      direction === "horizontal" ? [{
        type: 'slider',
        width: scrollBarSize.x,
        right: 0,
        yAxisIndex: 0,
        start: zoomStart,
        end: zoomEnd,
        opacity: 1,
        backgroundColor: 'transparent',
        borderColor: 'none',
        borderRadius: 0,
        brushStyle: false,
        handleSize: '0%',
        handleStyle: {
          color: 'transparent',
          borderColor: 'transparent',
        },
        zoomLock: true,
        brushSelect: false,
        showDetail: false,
        showDataShadow: false
      }, {
        type: 'inside',
        yAxisIndex: 0,
        backgroundColor: 'transparent',
        zoomOnMouseWheel: false,
        moveOnMouseMove: false,
        moveOnMouseWheel: true
      }] : [{
        type: 'slider',
        height: scrollBarSize.y,
        bottom: 0,
        xAxisIndex: 0,
        start: zoomStart,
        end: zoomEnd,
        opacity: 1,
        backgroundColor: 'transparent',
        borderColor: 'none',
        borderRadius: 0,
        brushStyle: false,
        handleSize: '0%',
        handleStyle: {
          color: 'transparent',
          borderColor: 'transparent',
        },
        zoomLock: true,
        brushSelect: false,
        showDetail: false,
        showDataShadow: false,
      }, {
        type: 'inside',
        xAxisIndex: 0,
        backgroundColor: 'transparent',
        zoomOnMouseWheel: false,
        moveOnMouseMove: false,
        moveOnMouseWheel: true,
      }]
    ),
    [direction, zoomStart, zoomEnd]
  )
	return useMemo(() => ({
    dataZoom,
    hasScroll: zoomStart !== undefined ? true : false,
		zoomStart, zoomEnd,
		onDataZoom
	}), [
    dataZoom,
    zoomStart, zoomEnd,
		onDataZoom
  ])
}